import React, { useEffect, useState } from 'react'

// Reads the app's current theme palette from CSS variables so chart colors
// stay theme-consistent instead of hardcoded hex. Shared by every chart
// widget instead of each one defining its own copy. Mirrors
// admin-dashboard's components/charts/chartUtils.ts.
export function useChartPalette() {
  const [palette, setPalette] = useState({
    accent: '#4F46E5',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    muted: '#6B7280',
    foreground: '#111827',
    surface: '#FFFFFF',
    border: '#E5E7EB',
  })

  useEffect(() => {
    const readPalette = () => {
      const styles = getComputedStyle(document.documentElement)
      const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback
      setPalette({
        accent: read('--accent', '#4F46E5'),
        success: read('--success', '#10B981'),
        warning: read('--warning', '#F59E0B'),
        danger: read('--danger', '#EF4444'),
        muted: read('--muted', '#6B7280'),
        foreground: read('--foreground', '#111827'),
        surface: read('--surface', '#FFFFFF'),
        border: read('--border', '#E5E7EB'),
      })
    }

    readPalette()

    const observer = new MutationObserver(readPalette)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  return palette
}

export interface ChartTooltipPayloadItem {
  color?: string
  name?: string | number
  value?: string | number
  dataKey?: string | number
}

export interface ChartTooltipContentProps {
  active?: boolean
  label?: string | number
  payload?: ChartTooltipPayloadItem[]
  labelFormatter?: (label: any) => React.ReactNode
  /** Per-series value formatter, keyed by dataKey (e.g. "$1,234.00" for a dollar series). Falls back to toLocaleString(). */
  valueFormatters?: Record<string, (value: number) => string>
}

// "$" + 2 decimal places, matching how dollar amounts are formatted
// elsewhere in this app (stat cards, etc).
export function formatCurrency(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// A custom recharts <Tooltip content={...}> renderer matching HeroUI Pro's
// ChartTooltip anatomy: a rounded card with a border and shadow, an
// optional muted header row for the x-axis label, then one row per series
// with a colored dot indicator, the series name, and its value right-
// aligned in tabular figures. Passed as `content` rather than relying on
// recharts' own contentStyle/labelStyle, which gives no control over this
// per-row layout. Border color is set inline (not a `border-*` className)
// because --border isn't registered under Tailwind's @theme here, so no
// `border-border` utility exists -- same reason every other chart color in
// this file comes from useChartPalette() rather than a class name.
export function ChartTooltipContent({ active, label, payload, labelFormatter, valueFormatters }: ChartTooltipContentProps) {
  const palette = useChartPalette()
  if (!active || !payload || payload.length === 0) return null

  return (
    <div
      className="min-w-[140px] rounded-xl bg-surface px-3 py-2 shadow-lg"
      style={{ border: `1px solid ${palette.border}` }}
    >
      {label !== undefined && (
        <div className="mb-1.5 text-xs font-medium text-muted">
          {labelFormatter ? labelFormatter(label) : label}
        </div>
      )}
      <div className="space-y-1">
        {payload.map((item, i) => {
          const formatter = item.dataKey !== undefined ? valueFormatters?.[item.dataKey] : undefined
          const formatted = typeof item.value === 'number'
            ? (formatter ? formatter(item.value) : item.value.toLocaleString())
            : item.value
          return (
            <div key={i} className="flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-muted">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                <span>{item.name}</span>
              </div>
              <span className="font-mono text-sm font-semibold tabular-nums text-foreground">{formatted}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// RPC dates come back as "YYYY-MM-DD". Short form for axis ticks, long form
// for the tooltip's label -- both parsed as local-midnight so the date
// doesn't shift a day in timezones behind UTC.
export function formatShortDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function formatLongDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// Recharts has no real empty-data layout: with data=[] it still renders
// axes/grid with a degenerate domain, and the hover cursor becomes a
// single band spanning the whole plot (sized off nothing), which can
// render past the container's bounds. Rendered in place of the chart
// entirely rather than trying to coax recharts into an empty state.
export function ChartEmptyState({ height }: { height: number }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 text-muted" style={{ height }}>
      <p className="text-sm font-medium">No data yet</p>
      <p className="text-xs">Data will appear here once it comes in</p>
    </div>
  )
}
